/**
 * verificationSlice — module-owned Redux slice for the shared verification aggregate (constitution
 * §67/68). Hybrid module/feature split: the module owns the slice + core selectors; detail features
 * own derived selectors (see `features/detail/data/verificationSelectors`).
 */
import { createSlice } from '@reduxjs/toolkit';
import { createEditTrackingReducers, createValidationReducers } from '../../../../app/sliceUtils';
import { initialState, VerificationSliceState } from './state';
import { buildVerificationExtraReducers } from './extraReducers';
import {
  setVerification,
  clearVerification,
  editVerification,
  updateBenefits,
  updatePayerCallInfo,
  updateBenefitsStatus,
  updateComboOfBenefitsIssue,
  updateVerifyAttributes,
  updateSingleCaseAgreement,
  updateAuthorization,
  addProcedure,
  removeProcedure,
  updateProcedure,
  moveProcedure,
  updateDiagnosisCodes,
  updateImplantsNotUsed,
  updatePatientResponsibility,
  updateApproval,
  applyReimbursementResults,
  setEstimateEvaluation,
  clearEstimateEvaluation,
  setDisableCodeSetMatching,
  appendCall,
  addNote,
  updateNote,
  removeNote,
  discardVerificationEdits,
} from './reducers';

const editTrackingReducers = createEditTrackingReducers<VerificationSliceState>();
const validationReducers = createValidationReducers<VerificationSliceState>();

const verificationSlice = createSlice({
  name: 'verification',
  initialState,
  reducers: {
    setVerification,
    clearVerification,
    editVerification,
    updateBenefits,
    updatePayerCallInfo,
    updateBenefitsStatus,
    updateComboOfBenefitsIssue,
    updateVerifyAttributes,
    updateSingleCaseAgreement,
    updateAuthorization,
    addProcedure,
    removeProcedure,
    updateProcedure,
    moveProcedure,
    updateDiagnosisCodes,
    updateImplantsNotUsed,
    updatePatientResponsibility,
    updateApproval,
    applyReimbursementResults,
    setEstimateEvaluation,
    clearEstimateEvaluation,
    setDisableCodeSetMatching,
    appendCall,
    addNote,
    updateNote,
    removeNote,
    ...editTrackingReducers,
    ...validationReducers,
    // Server-restoring discard (overrides the no-op edit-tracking `discardChanges`).
    discardChanges: discardVerificationEdits,
  },
  extraReducers: buildVerificationExtraReducers,
});

export const verificationActions = verificationSlice.actions;

export const {
  setVerification: setVerificationAction,
  clearVerification: clearVerificationAction,
  editVerification: editVerificationAction,
  updateBenefits: updateBenefitsAction,
  updatePayerCallInfo: updatePayerCallInfoAction,
  updateBenefitsStatus: updateBenefitsStatusAction,
  updateComboOfBenefitsIssue: updateComboOfBenefitsIssueAction,
  updateVerifyAttributes: updateVerifyAttributesAction,
  updateSingleCaseAgreement: updateSingleCaseAgreementAction,
  updateAuthorization: updateAuthorizationAction,
  addProcedure: addProcedureAction,
  removeProcedure: removeProcedureAction,
  updateProcedure: updateProcedureAction,
  moveProcedure: moveProcedureAction,
  updateDiagnosisCodes: updateDiagnosisCodesAction,
  updateImplantsNotUsed: updateImplantsNotUsedAction,
  updatePatientResponsibility: updatePatientResponsibilityAction,
  updateApproval: updateApprovalAction,
  applyReimbursementResults: applyReimbursementResultsAction,
  setEstimateEvaluation: setEstimateEvaluationAction,
  clearEstimateEvaluation: clearEstimateEvaluationAction,
  setDisableCodeSetMatching: setDisableCodeSetMatchingAction,
  appendCall: appendCallAction,
  addNote: addNoteAction,
  updateNote: updateNoteAction,
  removeNote: removeNoteAction,
  markFieldAsEdited: markVerificationFieldAsEditedAction,
  clearEditedFields: clearVerificationEditedFieldsAction,
  markAsSaved: markVerificationAsSavedAction,
  discardChanges: discardVerificationChangesAction,
  setValidationError: setVerificationValidationErrorAction,
  clearValidationError: clearVerificationValidationErrorAction,
  clearAllValidationErrors: clearAllVerificationValidationErrorsAction,
} = verificationSlice.actions;

export default verificationSlice.reducer;

export type { VerificationSliceState } from './state';
export { canCompleteBenefits } from './reducers';
export * from './selectors';
